import classNames from "classnames";
import "./OpticksPricingWidget.css";
import { formatCurrency } from "@/utilities/utils";
import OpticksButton from "../../OpticksButton/OpticksButton";
import OpticksButtonLink from "@/components/ui/OpticksButtonLink/OpticksButtonLink";
import { useLoaderStore } from "@/zustand/loaderStore";
import { useState, useRef, useEffect } from "react";
import usePaymentChargebee from "@/components/hooks/use-payment-chargebee/use-payment-chargebee";
import OpticksMessage from "../../OpticksMessage/OpticksMessage";
import type { Prices } from "@/types/model/Plan";
import type { FeatureOrSubviewId } from "@/components/get-feature/getFeatureConfig";
import { PLAN_VIEW_CONFIG } from "@/utilities/planHelpers";

interface Props {
  type: string;
  highlight?: string;
  typePill?: React.ReactNode;
  title: string;
  frequency?: string;
  currency?: string;
  prices?: Prices;
  hasDiscount?: boolean;
  originalPrice?: number | null;
  offersApplied?: boolean;
  hasHighlight?: boolean;
  features: {
    icon: React.ReactNode;
    title: string | React.ReactNode;
    subtitle?: string;
    badge?: React.ReactNode;
  }[];
  order?: number;
  currentPlanOrder?: number;
  isCurrentPlan?: boolean;
  scheduledPlanChange?: { type?: string } | null;
  endTermPlan?: string;
  planType?: string;
  featureScreen?: FeatureOrSubviewId | null;
}

const OpticksPricingWidget = ({
  type,
  highlight,
  typePill,
  title,
  frequency = "",
  currency = "",
  prices = {},
  hasDiscount = false,
  originalPrice = null,
  offersApplied = false,
  hasHighlight = false,
  features,
  order,
  currentPlanOrder,
  isCurrentPlan,
  scheduledPlanChange = null,
  endTermPlan = "",
  planType,
  featureScreen = null,
}: Props) => {
  const [isHovering, setIsHovering] = useState(false);
  const [displayPrice, setDisplayPrice] = useState(0);
  const priceRef = useRef<HTMLElement>(null);
  const { loading } = useLoaderStore((state) => state);
  const { openCheckout } = usePaymentChargebee();
  const isNextPlan = scheduledPlanChange?.type === type;
  const planConfig = PLAN_VIEW_CONFIG[type as keyof typeof PLAN_VIEW_CONFIG];
  const featureNotIncluded = (() => {
    if (!featureScreen || type === "custom") return false;
    let visibility = planConfig?.[featureScreen];
    if (!visibility && featureScreen.includes(".")) {
      const parentView = featureScreen.split(".")[0] ?? "";
      visibility = planConfig?.[parentView];
    }
    return visibility !== "visible";
  })();

  const animatePrice = (targetPrice: number, startPrice: number = 0) => {
    const duration = 400;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const value = startPrice + (targetPrice - startPrice) * progress;

      setDisplayPrice(value);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayPrice(targetPrice);
      }
    };

    animate();
  };

  useEffect(() => {
    if (type !== "custom" && prices[frequency]?.[currency] !== undefined) {
      const targetPrice = prices[frequency][currency];
      animatePrice(targetPrice, displayPrice);
    }
  }, [frequency, currency, prices]);

  return (
    <div
      className={classNames("opticks-pricing-widget", `type-${type}`)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {hasHighlight && (
        <div className={classNames("widget-header", { content: highlight })}>
          <b>{highlight}</b>
        </div>
      )}

      <div
        className={classNames("widget-content", `type-${type}`, {
          "offers-applied": offersApplied,
        })}
      >
        <div className="widget-titles">
          {typePill && <div className="type-pill">{typePill}</div>}
          <p className="title">{title}</p>
        </div>
        <div className="widget-price">
          {type !== "custom" ? (
            <>
              {hasDiscount && originalPrice !== null && (
                <div className="price-original-container">
                  <div className="price-original-value">
                    <b>{formatCurrency(originalPrice, currency, false)}</b>
                  </div>
                  <div className="discount-badge">
                    <b>Original Price</b>
                  </div>
                </div>
              )}
              <b className="price-value" ref={priceRef}>
                {formatCurrency(displayPrice, currency, false)}
              </b>
              <div className="period-taxes">
                <span className="period-value">
                  per {frequency === "yearly" ? "year" : "month"}
                </span>{" "}
                <span className="plus-taxes">+ taxes</span>
              </div>

              {!hasDiscount &&
                (frequency === "yearly" ? (
                  <div className="comparison-yearly" key={`yearly-${currency}`}>
                    <span className="comparison-content">
                      Save{" "}
                      {formatCurrency(
                        Number(
                          (prices.monthly?.[currency] ?? 0) * 12 -
                            (prices.yearly?.[currency] ?? 0)
                        ),
                        currency,
                        false
                      )}
                    </span>
                  </div>
                ) : (
                  <div style={{ height: "36px" }} />
                ))}
            </>
          ) : (
            <b className="price-value">Custom</b>
          )}
        </div>

        <div className="widget-features">
          {features.map((feature, index) => (
            <div className="point" key={index}>
              <div className="icon">{feature.icon}</div>
              <div className="text">
                <div className="title">
                  {feature.title}
                  {feature.badge && (
                    <span className="badge">{feature.badge}</span>
                  )}
                </div>
                {feature.subtitle && (
                  <span className="subtitle">{feature.subtitle}</span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="widget-actions">
          {/*
          ORDER:
          1. Custom (New Tab to Landing > Contact Us Form, "Contact Us")
          2. Lite / Pro / Max
            2.1. Message about Plan Modification (isNextPlan)
            2.2. Message about Current Plan (isCurrentPlan)
            2.3. Button for AppSumo clients (mailto Contact Sales, "Contact Sales")
            2.4. Button for Demo (currently unaccessible) + Fallback for Lite/Pro/Max if certain CB info is undefined (New Tab to Landing > Pricing, "Get Your Plan")
            2.5. Button for Lite/Pro/Max with Chargebee Modal on Subs. Management (copies about Upgrading or Downgrading plan depending on info received)
          */}
          {type === "custom" ? (
            <OpticksButtonLink
              variation={isHovering ? "soft" : "primary"}
              href="https://optickssecurity.com/contact-us"
              loading={loading}
              disabled={loading}
              label="Contact Us"
            />
          ) : isNextPlan ? (
            <OpticksMessage type="success">
              This plan will take place on: <br />
              <b>{endTermPlan}</b>
            </OpticksMessage>
          ) : isCurrentPlan ? (
            <OpticksButton
              label="This is your current plan"
              variation="ghost"
              disabled
            />
          ) : featureNotIncluded ? (
            <OpticksButton
              variation="secondary"
              disabled
              label="Feature not available on this plan"
            />
          ) : planType?.includes("sumo_plan") ? (
            <OpticksButtonLink
              variation={isHovering ? "soft" : "primary"}
              href="mailto:sales@optickssecurity.com"
              loading={loading}
              disabled={loading}
              label="Contact Sales"
            />
          ) : planType === "demo" ||
            (planType !== "free_trial" &&
              (currentPlanOrder === undefined || order === undefined)) ? (
            <OpticksButtonLink
              variation={isHovering ? "soft" : "primary"}
              href="https://optickssecurity.com/#pricing"
              loading={loading}
              disabled={loading}
              label="Get Your Plan"
            />
          ) : (
            <OpticksButton
              onClick={() =>
                openCheckout(
                  type as "free_trial" | "lite" | "pro" | "max",
                  currency,
                  frequency,
                  undefined,
                  {},
                  false
                )
              }
              variation={
                isHovering
                  ? "soft"
                  : order !== undefined &&
                      currentPlanOrder !== undefined &&
                      order < currentPlanOrder
                    ? "secondary"
                    : "primary"
              }
              loading={loading}
              disabled={loading}
              label={
                planType === "free_trial"
                  ? "Upgrade Your Plan"
                  : order !== undefined &&
                      currentPlanOrder !== undefined &&
                      order < currentPlanOrder
                    ? "Downgrade Your Plan"
                    : "Upgrade Your Plan"
              }
            />
          )}
        </div>
      </div>

      {hasHighlight && (
        <div className={classNames("widget-footer", { content: highlight })}>
          <b>{highlight}</b>
        </div>
      )}
    </div>
  );
};

export default OpticksPricingWidget;
